import { DropdownButtonType, List, ListItem, SingleSelectDropdown, StackingRootContext } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { imgPath } from '~commonInfra/utils/ImgUrl';
import { Messages } from '~src/codegen/Localize';
import { getAnalyticsExtensionConnectionIcon } from '~src/settings/extensions-settings/analytics/AnalyticsExtensionConnectionIcons';
import { IAnalyticsConnectionMetadata } from '~src/settings/types/ISiteSettingsForSiteAdmin';

const optionClass = style({ display: 'inline-flex', alignItems: 'center', gap: 6 });
const iconClass = style({ width: 20, height: 20 });
const labelClass = style({ fontWeight: 'bold', marginBottom: 6 });
const scopeClass = style({ marginTop: 6 });

export interface AnalyticsExtensionConnectionPickerProps {
  readonly connectionLuid: string;
  readonly currentConnection: IAnalyticsConnectionMetadata;
  readonly selectConnection: (connectionLuid: string) => void;
  readonly loadConnections: () => Promise<ReadonlyArray<IAnalyticsConnectionMetadata>>;
  readonly disabled?: boolean;
}

// Adapted from Maestro PR #5023's controlled picker; the caller owns the draft selection.
export const AnalyticsExtensionConnectionPicker: React.FC<AnalyticsExtensionConnectionPickerProps> = (props) => {
  const { loadConnections } = props;
  const popupRoot = React.useContext(StackingRootContext);
  const [connections, setConnections] = React.useState<ReadonlyArray<IAnalyticsConnectionMetadata>>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setLoadFailed(false);
    loadConnections().then(result => {
      if (mounted) {
        setConnections(result.filter(connection => connection.connection_brief.connection_type === 'TABPY'));
        setLoading(false);
      }
    }, () => {
      if (mounted) {
        setLoadFailed(true);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, [loadConnections]);

  const content = (connection: IAnalyticsConnectionMetadata) => (
    <span className={optionClass}>
      <img src={imgPath(getAnalyticsExtensionConnectionIcon(connection.connection_brief.connection_type))} alt="" className={iconClass} />
      {connection.connection_brief.connection_name}
    </span>
  );
  const items: ListItem[] = connections.map(connection => ({ value: connection.connection_luid, content: content(connection) }));
  const selectedItem = items.find(item => item.value === props.connectionLuid);
  const currentContent = props.currentConnection?.connection_luid === props.connectionLuid ? content(props.currentConnection) : null;
  const errorMessage = loadFailed ? Messages.flowAnalyticsExtensionConnectionsLoadError()
    : !loading && connections.length === 0 ? Messages.flowAnalyticsExtensionNoTabPyConnections() : '';

  return (
    <div>
      <StackingRootContext.Provider value={popupRoot}>
        <SingleSelectDropdown
          testId="flow-analytics-extension-dropdown"
          type={DropdownButtonType.Outline}
          label={<div className={labelClass}>{Messages.flowAnalyticsExtensionSelectServer()}</div>}
          value={selectedItem?.value}
          onChange={value => {
            if (connections.some(connection => connection.connection_luid === value)) {
              props.selectConnection(value);
            }
          }}
          list={<List items={items} />}
          disabled={props.disabled || loading || loadFailed || connections.length === 0}
          message={errorMessage}
          valid={loadFailed ? false : undefined}
        >
          {selectedItem?.content ?? currentContent ?? Messages.flowAnalyticsExtensionHostingServerPlaceholder()}
        </SingleSelectDropdown>
      </StackingRootContext.Provider>
      <div className={scopeClass}>{Messages.flowAnalyticsExtensionAppliesToAllScripts()}</div>
    </div>
  );
};
